import { TrafficTypeEnum, TTimeHHmm } from "@ryde/shared";

/**
 * A window between two whole hours-of-day, written the way the table and the ride caption show it.
 * Both ends are real `HH:mm` clock labels, so a range can never be built from an arbitrary string.
 */
export type TStationTimingHoursRange = `${TTimeHHmm}-${TTimeHHmm}`;

/** Minimal station shape the preview needs, so every editor can map its own stations onto it. */
export interface IStationTimingPreviewStation {
  id: number;
  name: string;
}

/** Which measurement of a leg an option applies. */
export enum StationTimingOptionKindEnum {
  Average = "Average",
  Median = "Median",
  /** Escape hatch back to the planned time; a native radio cannot be unchecked. */
  Planned = "Planned",
  TrafficSlot = "TrafficSlot",
}

export interface IStationTimingLegOption {
  /** Unique within the whole table; also the radio input value, and it encodes the leg index. */
  key: string;
  kind: StationTimingOptionKindEnum;
  /** Undefined only on the "leave this leg alone" option of a leg whose planned time is unknown. */
  seconds: number | undefined;
  datapoints: number;
  traffic?: TrafficTypeEnum;
  /** Local hours-of-day covered by a traffic slot option; empty for the overall options. */
  hours: number[];
}

/** How a measurement compares to the planned time; the only thing the "vs planned" pill colours by. */
export enum StationTimingDeltaToneEnum {
  Slower = "Slower",
  Faster = "Faster",
  /** Inside the tolerance — applying this measurement would not meaningfully move the plan. */
  OnPlan = "OnPlan",
  /** The leg carries no planned time, so there is nothing to compare against. */
  NoPlan = "NoPlan",
}

/** The comparison of one measurement against the planned time of its leg. */
export interface IStationTimingDelta {
  tone: StationTimingDeltaToneEnum;
  /** Signed seconds against the plan; `undefined` on a leg with no planned time. */
  seconds: number | undefined;
  /** Signed share of the planned time; `undefined` on a leg with no planned time to divide by. */
  percent: number | undefined;
  /** The measurement itself, so a leg with no plan can still show what was measured. */
  measuredSeconds: number;
}

/** The options of one leg, grouped the way the row renders them. */
export interface ILegOptionSet {
  /** The "leave this leg alone" escape hatch; absent only on a leg with no measurements at all. */
  plannedOption: IStationTimingLegOption | undefined;
  medianOption: IStationTimingLegOption | undefined;
  averageOption: IStationTimingLegOption | undefined;
  trafficOptions: IStationTimingLegOption[];
  /** Every option of the leg in render order; the selection utils resolve keys against it. */
  options: IStationTimingLegOption[];
}

export const EMPTY_LEG_OPTION_SET: ILegOptionSet = {
  plannedOption: undefined,
  medianOption: undefined,
  averageOption: undefined,
  trafficOptions: [],
  options: [],
};

/**
 * One informational day-of-week line of a leg. Deliberately carries no option key: applying stays a
 * per-leg choice between the overall average, the overall median and a traffic slot.
 */
export interface IStationTimingDayRow {
  /** 0 = Sunday .. 6 = Saturday, in the company timezone. */
  dayOfWeek: number;
  datapoints: number;
  avgSeconds: number;
  medianSeconds: number;
}

export interface IStationTimingLegRow extends ILegOptionSet {
  legIndex: number;
  stationFromName: string;
  stationToName: string;
  plannedSeconds: number | undefined;
  datapoints: number;
  avgSeconds: number | null;
  medianSeconds: number | null;
  p10Seconds: number | null;
  p90Seconds: number | null;
  /** Ordered Sunday-first; empty when the flow did not ask for the breakdown or no day qualified. */
  dayRows: IStationTimingDayRow[];
  /** False when the sample stayed below the backend threshold — the row renders read-only. */
  hasData: boolean;
}

/** The legs split for rendering only; the selection and the counts still run over the whole list. */
export interface IStationTimingLegRowGroups {
  rowsWithData: IStationTimingLegRow[];
  rowsWithoutData: IStationTimingLegRow[];
}

export interface IStationTimingPreviewSelection {
  /**
   * Ordered leg seconds ready to apply, always `stations.length - 1` entries: the measurement the user
   * picked, or `undefined` on a leg they left alone. `undefined` means "keep whatever this leg spans
   * right now" — writing the planned value back instead would silently revert unsaved manual edits.
   */
  legSeconds: (number | undefined)[];
  /** Indexes of the legs the user actually picked a measurement for. */
  selectedLegIndexes: number[];
}

/** Selected option key per leg index; a missing entry means "keep the planned time". */
export type TStationTimingSelectionMap = Record<number, string>;

/** Leg indexes whose day-of-week breakdown is currently revealed; purely a display state. */
export type TStationTimingExpandedDayLegs = Record<number, boolean>;
